<template>
  <div class="musubi-page musubi-page--senders">
    <header class="musubi-page__head">
      <h1>Sender identities</h1>
      <p class="text-muted">
        Verified from-addresses for outbound email. The default identity is the
        fallback when a campaign or template doesn't pin a specific one. Use
        <strong>Verify</strong> to DNS-check the domain's SPF + DKIM records.
      </p>
      <p class="text-muted small mb-0">
        A sender can only use an address on one of this workspace's own sending domains (listed under
        Sending domains below). While a domain is added but not yet verified, a sender on it can be saved,
        but it cannot be the default and nothing is sent from it until the domain is verified.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />New sender
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- Workspace-default reply forwarding — used when a sender has no override. -->
    <section class="fwd-default card mb-3">
      <div class="card-body">
        <h6 class="mb-1"><CygnusIcon name="envelope" class="me-1" />Default reply forwarding</h6>
        <p class="small text-muted mb-2">
          Where replies to any sender without its own forwarding address are delivered. Replies stay tracked in
          Yorlin (stop-on-reply + AI triage still fire); the prospect is set as Reply-to so you answer directly.
          Leave blank to turn forwarding off unless a sender overrides it.
        </p>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <input v-model="forwardingDefault" type="email" class="form-control form-control-sm" style="max-width:320px" placeholder="you@yourcompany.com">
          <button class="btn btn-primary btn-sm" :disabled="busy.fwd" @click="saveForwardingDefault">
            <CygnusIcon name="spinner" :spin="true" v-if="busy.fwd" /><span v-else>Save default</span>
          </button>
          <span v-if="forwardingMsg" class="small text-muted">{{ forwardingMsg }}</span>
        </div>
      </div>
    </section>

    <!-- Editor (create OR edit existing) -->
    <section v-if="editor" class="editor card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">{{ editor.id ? `Edit sender #${editor.id}` : 'New sender' }}</h6>
          <button class="btn btn-link btn-sm" @click="closeEditor">Close</button>
        </div>

        <form @submit.prevent="saveSender">
          <div class="row g-2 mb-2">
            <div class="col-md-4">
              <label class="form-label small text-muted">Label *</label>
              <input v-model="editor.label" type="text" required class="form-control form-control-sm" placeholder="Apexl Marketing">
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">From name *</label>
              <input v-model="editor.from_name" type="text" required class="form-control form-control-sm" placeholder="Apexl Studios">
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">From email *</label>
              <input v-model="editor.from_email" type="email" required class="form-control form-control-sm" placeholder="marketing@apexlstudios.com">
              <div class="form-text small">Must be on a verified sending domain of this workspace.</div>
              <div v-if="editor.domain_status && !canSend(editor.domain_status)" class="small text-danger">
                {{ domainStatusText(editor.domain_status) }}
              </div>
            </div>
          </div>

          <div class="row g-2 mb-2 align-items-end">
            <div class="col-md-4">
              <label class="form-label small text-muted">Reply-to</label>
              <input v-model="editor.reply_to" type="email" class="form-control form-control-sm" placeholder="(defaults to the from address)">
              <div class="form-text small">
                Usually left empty. If set, it must be on one of this workspace's sending domains that takes replies:
                added with receiving switched on, its MX records verified, and its reply route set up by Yorlin.
                Otherwise replies would not come back to Yorlin and follow-ups would not stop when someone replies.
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Forward replies to</label>
              <input v-model="editor.forwarding_address" type="email" class="form-control form-control-sm" placeholder="you@yourcompany.com">
            </div>
            <div class="col-md-3 d-flex gap-3">
              <div class="form-check">
                <input id="sender-default" v-model="editor.is_default" type="checkbox" class="form-check-input">
                <label class="form-check-label small" for="sender-default">Default</label>
              </div>
              <div class="form-check">
                <input id="sender-active" v-model="editor.is_active" type="checkbox" class="form-check-input">
                <label class="form-check-label small" for="sender-active">Active</label>
              </div>
            </div>
          </div>

          <!-- Forwarding keeps tracking, stop-on-reply and triage. A Reply-To
               on a domain that does not bring replies back would bypass them,
               so the API only accepts one on a domain that takes replies. -->
          <div class="fwd-note small">
            <CygnusIcon name="info-circle" />
            <span>
              <strong>Forwarding</strong> delivers each reply to this inbox while Yorlin keeps tracking it:
              stop-on-reply and AI triage still work, and the original sender is set as <em>Reply-to</em> so you
              answer them directly from your inbox. Leave it blank to use the workspace default below.
              To send replies to your own inbox, use forwarding rather than a Reply-to on another domain.
            </span>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.save || !editor.label || !editor.from_name || !editor.from_email">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.save" />
              <span v-else>{{ editor.id ? 'Save changes' : 'Create sender' }}</span>
            </button>
            <button
              v-if="editor.id"
              type="button"
              class="btn btn-outline-danger btn-sm ms-auto"
              @click="deleteSender(editor.id)"
            >
              <CygnusIcon name="trash" class="me-1" />Delete
            </button>
          </div>
        </form>

        <!-- DKIM/SPF verification (edit mode only) -->
        <div v-if="editor.id" class="verify-panel mt-4">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <h6 class="small text-muted text-uppercase mb-0">Domain auth</h6>
            <span :class="['badge', badgeClass(editor.dkim_status)]">DKIM: {{ editor.dkim_status }}</span>
            <span :class="['badge', badgeClass(editor.spf_status)]">SPF: {{ editor.spf_status }}</span>
            <span v-if="editor.verified_at" class="small text-muted">checked {{ fmtDate(editor.verified_at) }}</span>
            <button class="btn btn-outline-secondary btn-sm ms-auto" :disabled="busy.verify" @click="verify(editor.id)">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.verify" />
              <CygnusIcon name="shield" class="me-1" v-else />Verify now
            </button>
          </div>
          <div v-if="lastAuth" class="small text-muted mt-2">
            Domain <code>{{ lastAuth.domain }}</code> —
            SPF {{ lastAuth.spf ? '✓' : '✗' }},
            DKIM {{ lastAuth.dkim ? `✓ (${lastAuth.dkim_selector})` : '✗' }},
            DMARC {{ lastAuth.dmarc ? `✓ (${lastAuth.dmarc_policy || 'p=none'})` : '✗' }}.
            <span v-if="!lastAuth.spf || !lastAuth.dkim">Add the missing record(s) at your DNS provider (or in Mailgun's domain setup), then re-verify.</span>
          </div>
        </div>
      </div>
    </section>

    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.senders"
      :initial-sort="{ column: 'is_default', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
      @row-click="onRowClick"
    />

    <p v-if="lastMessage" class="actions-bar__msg mt-2 small">{{ lastMessage }}</p>

    <SendingDomains />

    <BuyDomain />

    <SendSettingsPanel />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc } from "./util/cellHtml";

export default {
  name: 'AdminSenders',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      busy: { load: false, save: false, verify: false, fwd: false },
      error: null,
      lastMessage: '',
      lastAuth: null,
      editor: null,
      forwardingDefault: '',
      forwardingMsg: '',
    };
  },
  computed: {
    columns() {
      const badge = (v) => {
        const cls = v === 'verified' ? 'background:#DCFCE7;color:var(--m-status-success,#166534)'
          : v === 'failed' ? 'background:#FEE2E2;color:var(--m-status-danger,#991B1B)'
          : 'background:#E5E7EB;color:var(--m-text-secondary,#374151)';
        return `<span style="padding:1px 7px;border-radius:100px;font-size:0.7rem;${cls}">${esc(v)}</span>`;
      };
      return [
        { key: 'id',          label: 'ID',         width: 60,  sorter: 'number' },
        { key: 'label',       label: 'Label' },
        { key: 'from_name',   label: 'From name' },
        { key: 'from_email',  label: 'From email' },
        { key: 'dkim_status', label: 'DKIM',       width: 110, format: badge },
        { key: 'spf_status',  label: 'SPF',        width: 110, format: badge },
        { key: 'is_default',  label: 'Default',    width: 90,  hozAlign: 'center',
          format: (v) => (v ? '✓' : '—') },
        { key: 'domain_status', label: 'Can send', width: 150,
          format: (v) => {
            const ok = this.canSend(v);
            const style = ok ? 'background:#DCFCE7;color:var(--m-status-success,#166534)' : 'background:#FEE2E2;color:var(--m-status-danger,#991B1B)';
            return `<span style="padding:1px 7px;border-radius:100px;font-size:0.7rem;${style}">${esc(ok ? 'Yes' : this.domainStatusShort(v))}</span>`;
          } },
      ];
    },
  },
  mounted() {
    this.refresh();
    this.loadForwardingDefault();
    // Deep-link support: /admin/senders?identity=<id> opens that identity's
    // editor directly (used by the deliverability page's "Edit identity" link).
    const deepLinked = Number(this.$route?.query?.identity);
    if (deepLinked) this.onRowClick({ id: deepLinked });
  },
  methods: {
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleString() : '—'; },
    // Send safety slice 4: the server's verdict on the sender's From domain.
    canSend(status) { return status === 'own_verified' || status === 'platform_domain'; },
    domainStatusShort(status) {
      return {
        own_unverified: 'Domain not verified',
        other_workspace: 'Not your domain',
        untracked: 'Domain not added',
        invalid: 'No domain',
      }[status] || 'No';
    },
    domainStatusText(status) {
      return {
        own_unverified: 'This domain is added but not verified yet. Nothing is sent from this sender until it is.',
        other_workspace: 'This domain is not one of this workspace\'s sending domains, so nothing is sent from this sender.',
        untracked: 'This domain has not been added under Sending domains, so nothing is sent from this sender.',
        invalid: 'This address has no domain, so nothing is sent from this sender.',
      }[status] || '';
    },
    async loadForwardingDefault() {
      try {
        const res = await this.api.get('/api/v1/admin/senders/forwarding');
        const body = res?.data ?? res;
        this.forwardingDefault = body?.forwarding_address || '';
      } catch (e) { /* non-fatal — the panel just starts empty */ }
    },
    async saveForwardingDefault() {
      this.busy.fwd = true; this.forwardingMsg = '';
      try {
        await this.api.put('/api/v1/admin/senders/forwarding', { forwarding_address: this.forwardingDefault || null });
        this.forwardingMsg = this.forwardingDefault ? 'Default forwarding saved.' : 'Default forwarding cleared.';
      } catch (e) {
        this.forwardingMsg = e?.response?.data?.error || e?.message || 'Could not save.';
      }
      this.busy.fwd = false;
    },
    badgeClass(s) {
      return s === 'verified' ? 'bg-success' : (s === 'failed' ? 'bg-danger' : 'bg-secondary');
    },
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/senders');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load senders.'; }
      this.busy.load = false;
    },
    openCreate() {
      this.editor = { label: '', from_name: '', from_email: '', reply_to: '', forwarding_address: '', is_default: false, is_active: true };
      this.lastAuth = null; this.lastMessage = '';
    },
    closeEditor() { this.editor = null; this.lastAuth = null; },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/senders/${row.id}`);
        const s = (res?.data ?? res)?.sender;
        if (!s) return;
        this.editor = {
          id: s.id, label: s.label || '', from_name: s.from_name || '', from_email: s.from_email || '',
          reply_to: s.reply_to || '', forwarding_address: s.forwarding_address || '',
          is_default: !!s.is_default, is_active: !!s.is_active,
          dkim_status: s.dkim_status, spf_status: s.spf_status, verified_at: s.verified_at,
          domain_status: s.domain_status,
        };
        this.lastAuth = null; this.lastMessage = '';
      } catch (e) { this.error = e?.message || 'Failed to open sender.'; }
    },
    async saveSender() {
      this.busy.save = true; this.error = null;
      try {
        const body = {
          label: this.editor.label,
          from_name: this.editor.from_name,
          from_email: this.editor.from_email,
          reply_to: this.editor.reply_to || null,
          forwarding_address: this.editor.forwarding_address || null,
          is_default: this.editor.is_default,
          is_active: this.editor.is_active,
        };
        let s;
        if (this.editor.id) {
          s = (await this.api.put(`/api/v1/admin/senders/${this.editor.id}`, body));
          this.lastMessage = `Sender #${this.editor.id} updated.`;
        } else {
          s = (await this.api.post('/api/v1/admin/senders', body));
        }
        const payload = s?.data ?? s;
        if (payload?.sender?.id) {
          const sender = payload.sender;
          this.editor = {
            ...this.editor, id: sender.id,
            dkim_status: sender.dkim_status, spf_status: sender.spf_status, verified_at: sender.verified_at,
            is_default: !!sender.is_default, domain_status: sender.domain_status,
          };
          if (!this.lastMessage) this.lastMessage = `Sender #${sender.id} created.`;
        } else {
          this.error = payload?.error || 'Save failed.';
        }
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
    async verify(id) {
      this.busy.verify = true; this.error = null; this.lastAuth = null;
      try {
        const res = await this.api.post(`/api/v1/admin/senders/${id}/verify`, {});
        const payload = res?.data ?? res;
        if (payload?.sender) {
          this.editor.dkim_status = payload.sender.dkim_status;
          this.editor.spf_status  = payload.sender.spf_status;
          this.editor.verified_at = payload.sender.verified_at;
          this.lastAuth = payload.auth || null;
        } else {
          this.error = payload?.error || 'Verify failed.';
        }
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Verify failed.';
      }
      this.busy.verify = false;
    },
    async deleteSender(id) {
      if (!confirm(`Delete sender #${id}? Blocked if it's the default or in use by a campaign/drip.`)) return;
      this.error = null;
      try {
        await this.api.delete(`/api/v1/admin/senders/${id}`);
        this.closeEditor();
        this.lastMessage = `Sender #${id} deleted.`;
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Delete failed.';
      }
    },
  },
};
</script>

<style>
.musubi-page--senders { padding: var(--m-space-6, 32px); }
.musubi-page--senders .actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.musubi-page--senders .actions-bar__msg { color: var(--m-text-secondary, #475569); }
.musubi-page--senders .editor { background: var(--m-bg-card, #FFF); }
.musubi-page--senders .verify-panel { border-top: 1px solid var(--m-border, #E2E8F0); padding-top: 14px; }
.musubi-page--senders .fwd-note { display: flex; gap: 8px; align-items: flex-start; margin-top: 4px; color: var(--m-text-secondary, #475569); line-height: 1.5; }
.musubi-page--senders .fwd-note i { color: var(--m-status-info, #0284C7); margin-top: 2px; flex: none; }
.musubi-page--senders .fwd-default { background: var(--m-bg-card, #FFF); }
</style>
